import { AppBar, styled } from '@material-ui/core';
import { attributes, Events, refTo } from '@type-r/models';
import { useIO, useLinked, useModel } from '@type-r/react';
import { CompanyModel, globalStore, PlatformCompany, RelationshipModel } from 'api';
import { AllActivitySection } from 'common/activities/activitySection';
import { RemittanceDocumentLink } from 'common/activities/details/remittanceDocumentLink';
import { asCurrency, Button, CollapsableSection, Column, Columns, Flex, FormInput, FormSelect, getBackPath, Link, Loading, navigate, Option, PrimaryButton, ReadOnly, SelectFile, Toolbar } from 'lqx-react';
import * as React from 'react';
import { asTimelessLinkedDate } from 'src/app/pages/digitization/shared';
import { RemittanceAdvice } from 'src/app/pages/inmatch/api';
import { ResolvePanel } from '../../bank-transactions/unresolved/details';
import { PickCompany } from '../../bank-transactions/unresolved/pickCompany';
import { ViewEmail } from '../viewEmail';
import { InvoicesList } from './invoices-list';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';

const State = attributes({
    customer : refTo( PlatformCompany ),
    supplier : refTo( PlatformCompany ),
});

export const RemittanceAdviceDetails = ({ $remittanceAdviceId, store, onSuccess, navigateToOnSave }) => {    
    const id = $remittanceAdviceId.value,
        isCreate = id === 'create';

    const remittanceAdvice = useModel(RemittanceAdvice);
    remittanceAdvice['_defaultStore'] = store;

    const { activities, companies } = store;

    const $doSubmitIO = useLinked(false);

    const isReady = useIO(async () => {
        if (!companies.length) {
            await store.companies.fetch();
        }

        if ( !isCreate && id !== 'true') {
            remittanceAdvice.id = id;
            await remittanceAdvice.fetch()
                .catch(e => {
                    navigate("/remittance-advices");
                });
            
            await activities.fetch({params: {assetId: id, assetType: 'RemittanceAdvice'}});
                console.log("activities->", activities);
        }

        $editButtonClicked.false();
        $isDisabled.false();
        $file.null();
    }, [id]);

    const $isDisabled = useLinked(false);

    const $editButtonClicked = useLinked(false);

    const $file = useLinked( null );

    return isReady && !$doSubmitIO.value ? (
        <ReadOnly.Provider value={false} >
            <AppBar position="static" color="transparent">
                {Can(UserEntitlementCatalogs.BankEntryWrite) && 
                <Toolbar>
                    { id ? !remittanceAdvice.isReconciled && 'Edit ' : 'Create '} Remittance Advice
                    <Flex grow />

                    { id && !remittanceAdvice.isReconciled ?
                        <Button onClick={() => $editButtonClicked.set(true)}>
                            Edit
                        </Button> : 
                        undefined }

                    <Button qa-id="cancel">
                        <Link 
                            href={ getBackPath() } 
                            onClick={() => $remittanceAdviceId.null()}
                        >Cancel</Link>
                    </Button>
                    <PrimaryButton
                        disabled={$isDisabled.value}
                        onClick={async () => {
                            $doSubmitIO.true();
                            $editButtonClicked.false();
                            await remittanceAdvice.save({params: {file: $file.value }});
                            
                            $doSubmitIO.false();
                            Events.trigger("raSummary:refresh");
                            // if (navigateToOnSave) {
                            //     navigate(`${navigateToOnSave}/${remittanceAdvice.id}`);
                            // } else {
                            //     back();
                            // }

                            onSuccess(remittanceAdvice);
                            Events.trigger("totals:refresh");
                        }}>
                        Submit
                    </PrimaryButton>
                </Toolbar>
                }
            </AppBar>
            <RemittanceAdviceContent 
                remittanceAdvice={remittanceAdvice}
                companies={companies} 
                $editButtonClicked={$editButtonClicked}
                activities={activities}
                $isDisabled={$isDisabled}
                $file={$file}
            />
        </ReadOnly.Provider>
    ) :
        <Loading />
}

export const RemittanceAdviceContent = ({ remittanceAdvice, companies, $editButtonClicked, activities = undefined, $isDisabled, invoices = undefined, $file }) => {
    const state = useModel( State );
    const instruments = ['ACH', 'Check', 'Cross-Border Check', 'Wire'];
    const inputDisabled : boolean = (!$editButtonClicked.value && !!remittanceAdvice.id) || !remittanceAdvice.supplierPlatformCompanyId || !remittanceAdvice.customerPlatformCompanyId;
    const $availableCurrencies = useLinked(undefined);

    const [remittanceAdviceCopy, setRemittanceAdviceCopy] = React.useState<RemittanceAdvice>();

    const isReady = useIO(async () => {

        if (invoices && invoices.length) {
            remittanceAdvice.set({
                ...remittanceAdvice, 
                supplierPlatformCompanyId: invoices.first().supplierPlatformCompanyId,
                customerPlatformCompanyId: invoices.first().customerPlatformCompanyId});

            setCompaniesState(remittanceAdvice);
        }

        if (remittanceAdvice && remittanceAdvice.id) {
            
            setRemittanceAdviceCopy(remittanceAdvice.clone());

            setCompaniesState(remittanceAdvice);
        }
    }, []);

    const setCompaniesState = (remittanceAdvice) => {
        if (remittanceAdvice.supplierPlatformCompanyId) {
            const supplier = companies.find(company => company.id === remittanceAdvice.supplierPlatformCompanyId);
            state.$.supplier.set(supplier);
        }

        if (remittanceAdvice.customerPlatformCompanyId) {
            const customer = companies.find(company => company.id === remittanceAdvice.customerPlatformCompanyId);
            state.$.customer.set(customer);
        }
    };

    const { supplierPlatformCompanyId, customerPlatformCompanyId } = remittanceAdvice;

    const isCollectionReady = async (stateCollection, stateMatches, incommingCollection) =>{
        if (supplierPlatformCompanyId && customerPlatformCompanyId ) {
            await stateCollection.fetch({ params: { supplierPlatformCompanyId, customerPlatformCompanyId } });

            if (incommingCollection && incommingCollection.length) {
                const selected = stateCollection
                    .filter(inv => incommingCollection.map(i => i.id).includes(inv.id))
                    .map(inv => {
                        const copy = inv.clone();
                        copy.paymentAmount = inv.outstandingAmount;
                        return copy;
                    });

                    stateMatches.set(selected);
            }

        } else if (stateCollection) {
            stateCollection.set(undefined);
        }
    }

    const areInvoicesReady = useIO(async () => {
        await isCollectionReady(remittanceAdvice.invoices, remittanceAdvice.matches, invoices);
        if (invoices && invoices.length) {
            remittanceAdvice.currency = invoices.first().currency;
        }

        $availableCurrencies.set(Array.from(new Set(remittanceAdvice.invoices.map(inv => inv.currency))));
    }, [supplierPlatformCompanyId, customerPlatformCompanyId, isReady]);

    const companyUpdated = () => {
        
        if (state.customer) {
            remittanceAdvice.$.customerPlatformCompanyId.set(Number(state.customer.id));
        } else {
            remittanceAdvice.$.customerPlatformCompanyId.set(null);
            remittanceAdvice.currency = null;
            $availableCurrencies.set(null);
        }

        if (state.supplier) {
            remittanceAdvice.$.supplierPlatformCompanyId.set(Number(state.supplier.id));
        } else {
            remittanceAdvice.$.supplierPlatformCompanyId.set(null);
            remittanceAdvice.currency = null;
            $availableCurrencies.set(null);
        }
    }

    const [changedAttributes, setChangedAttributes] = React.useState([]);

    React.useEffect(() => {
        if ($editButtonClicked.value) {
            Object.keys(remittanceAdvice.changedAttributes()).map(key => {
                if (remittanceAdvice[key] === remittanceAdviceCopy[key]) {
                    setChangedAttributes(changedAttributes.filter(value => { return value != key }));
                } else {
                    setChangedAttributes([...changedAttributes, key]);
                }
            });
        }
    }, [remittanceAdvice['_changeToken']]);

    React.useEffect(() => {
        if ($editButtonClicked.value) {
            let changes = changedAttributes;

            if (!remittanceAdvice.matches.every(inv => {return remittanceAdviceCopy.matches.map(m => m.invoiceNumber).includes(inv.invoiceNumber)})
                || remittanceAdvice.matches.length !== remittanceAdviceCopy.matches.length) {
                    changes = [...changes, 'matches'];
            } else {
                changes = changes.filter(value => { return value != 'matches' });
            }

            remittanceAdvice.matches.map(inv => Object.keys(inv.changedAttributes()).map(key => {
                if (!remittanceAdviceCopy.matches.get(inv.id) || inv[key] === remittanceAdviceCopy.matches.get(inv.id)[key] 
                    || (!inv[key] && !remittanceAdviceCopy.matches.get(inv.id)[key])) {
                    changes = changes.filter(value => { return value != `${inv.invoiceNumber}.${key}` });
                } else {
                    changes = [...changes, `${inv.invoiceNumber}.${key}`];
                }
            }));
            setChangedAttributes(changes);
        }
    }, [remittanceAdvice.matches['_changeToken']]);

    React.useEffect(() => {
        const isDisabled = (!$editButtonClicked.value && !!remittanceAdvice.id) || !remittanceAdvice.isValid() || ($editButtonClicked.value && !changedAttributes.length);

        $isDisabled.set(isDisabled);
    }, [$editButtonClicked, remittanceAdvice['_changeToken'], changedAttributes]);

    React.useEffect(() => {
        setChangedAttributes([...changedAttributes, 'file']);
    }, [$file]);

    React.useEffect(() => {
        if ($availableCurrencies.value && $availableCurrencies.value.length === 1) {
            remittanceAdvice.currency = $availableCurrencies.value[0];
        }
    }, [$availableCurrencies]);

    return (isReady ? <>
        <CollapsableSection title="Details">
            <Columns>
                <Column>
                    <FormInput label="Remittance Amount" readOnly positive integer value={asCurrency(remittanceAdvice.totalInvoiceAmount, remittanceAdvice.currency)} />
                    <FormSelect label="Currency" disabled={!$availableCurrencies.value || inputDisabled || remittanceAdvice.matches.length} $value={remittanceAdvice.$.currency} required>
                        {$availableCurrencies.value && $availableCurrencies.value.map(currency => {
                            return (
                                <Option key={currency} value={currency}>{currency}</Option>
                            );
                        })}
                    </FormSelect>
                    <FormSelect label="Instrument" disabled={inputDisabled} $value={remittanceAdvice.$.instrument}>
                        {instruments.map(ins => {
                            return (
                                <Option key={ins} value={ins}>{ins}</Option>
                            );
                        })}
                    </FormSelect>
                    { remittanceAdvice.originalFileUrl ? <RemittanceDocumentLink label="Source file" remittanceDocument={remittanceAdvice.originalFileUrl} originalFileName={remittanceAdvice.originalFileName}/> : 
                        undefined }
                    <PickCompany disabled={!!remittanceAdvice.matches.length || (remittanceAdvice.id && !$editButtonClicked.value)} placeholder="Select Supplier Company" 
                        $value={ state.$.supplier } 
                        companies={companies} 
                        filter={company => !state.customer || company.id !== state.customer.id}
                        customer={state.customer} 
                        onChange={companyUpdated} />
                </Column>
                <Column>
                    <FormInput label="Payment Execution Date" disabled={inputDisabled} date $value={asTimelessLinkedDate(remittanceAdvice.$.executionDate)} />
                    <FormInput label="Estimated Payment Value Date" disabled={inputDisabled} date $value={asTimelessLinkedDate(remittanceAdvice.$.estimatedPaymentDate)} />
                    <FormInput label="Payment Ref.#" disabled={inputDisabled} $value={remittanceAdvice.$.referenceNumber} />
                    { remittanceAdvice.originalFileUrl ? <FormInput label="" aria-label="Empty" /> : !inputDisabled && <FormInput label="" aria-label="Empty" /> }
                    <PickCompany disabled={!!remittanceAdvice.matches.length || (remittanceAdvice.id && !$editButtonClicked.value)} placeholder="Select Customer Company" 
                        $value={ state.$.customer } 
                        supplier={ state.supplier }
                        companies={companies} 
                        filter={company => !state.supplier || company.id !== state.supplier.id} 
                        onChange={companyUpdated} />
                </Column>
            </Columns>
        </CollapsableSection>

        
        { remittanceAdvice.emailMetadata && remittanceAdvice.emailMetadata.from ? 
            <ViewEmail emailInfo={remittanceAdvice.emailMetadata} /> : undefined }

        <InvoicesList remittanceAdvice={remittanceAdvice} loading={!areInvoicesReady} noEdits={ inputDisabled || !remittanceAdvice.currency }/>
        
        <CollapsableSection title="Transaction Details">
            <Columns>
                <Column>
                    <FormInput readOnly label="Total Invoice Payment Amount" value={asCurrency(remittanceAdvice.totalInvoiceAmount, remittanceAdvice.currency)} />
                    <FormInput positive number label="Amount Credited" disabled={inputDisabled} $value={remittanceAdvice.$.creditedAmount} />
                </Column>
                <Column>
                    <FormInput label="Net Amount" readOnly value={asCurrency(remittanceAdvice.netAmount, remittanceAdvice.currency)} />
                    <FormInput positive number label="Processing Fees" disabled={inputDisabled} $value={remittanceAdvice.$.processingFee} /></Column>
            </Columns>
        </CollapsableSection>

        {activities && activities.length ? <AllActivitySection activities={activities} currency={remittanceAdvice.currency} /> : undefined}
    </> : <Loading/>)
}

const SelectRAFile = styled(SelectFile)({
    padding: 4,
    marginTop: 0
});
